import { TextField } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';

// Same text size as the shared column filters (FilterDropdown, NumericColumnFilter).
const FILTER_TEXT_SIZE = '11px';

const PICKER_TEXT_SIZE = '12px';

// MUI X v8 pickers render the value as sections, not as an <input>, so the size is set on the
// section classes too.
const pickerSlotProps = (placeholder: string) => ({
  field: { clearable: true },
  textField: {
    variant: 'standard' as const,
    size: 'small' as const,
    placeholder,
    sx: {
      minWidth: 0,
      '& input, & .MuiPickersInputBase-root, & .MuiPickersSectionList-root, & .MuiPickersSectionList-section':
        { fontSize: PICKER_TEXT_SIZE },
      '& .MuiSvgIcon-root': { fontSize: '16px' },
    },
    onKeyDown: (event: React.KeyboardEvent) => event.stopPropagation(),
  },
  // The calendar and clock popup.
  layout: {
    sx: {
      '& .MuiTypography-root, & .MuiPickersDay-root, & .MuiPickersCalendarHeader-label, & .MuiPickersYear-yearButton, & .MuiMultiSectionDigitalClockSection-item, & .MuiButton-root':
        { fontSize: PICKER_TEXT_SIZE },
    },
  },
});

type NumberInputFilterProps = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
};

/**
 * A number input. The value is committed on Enter or when the field loses focus, like the inputs of
 * NumericColumnFilter, so the backend is not called for each key.
 */
export const NumberInputFilter = ({
  value,
  onChange,
}: NumberInputFilterProps) => {
  const [text, setText] = useState(value ?? '');
  const [committed, setCommitted] = useState(value);

  // Show "Clear all filters" and other outside changes in the field.
  if (value !== committed) {
    setCommitted(value);
    setText(value ?? '');
  }

  const commit = () => {
    const next = text.trim() === '' ? undefined : text.trim();
    if (next !== value) onChange(next);
  };

  return (
    <TextField
      type="number"
      size="small"
      variant="standard"
      placeholder="Number"
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter') commit();
      }}
      slotProps={{
        htmlInput: { inputMode: 'numeric', sx: { fontSize: FILTER_TEXT_SIZE } },
      }}
    />
  );
};

/** A date range. A missing end is open. */
export type DateRangeFilterValue = { from?: Date; to?: Date };

type DateRangeFilterProps = {
  value: DateRangeFilterValue | undefined;
  onChange: (value: DateRangeFilterValue | undefined) => void;
  withTime?: boolean;
};

const toDayjs = (date: Date | undefined): Dayjs | null =>
  date ? dayjs(date) : null;

const toDate = (value: Dayjs | null): Date | undefined =>
  value?.isValid() ? value.toDate() : undefined;

/**
 * From/to date pickers (MUI X, the same pickers as the simulation list date filters).
 * With `withTime`, the pickers also select hours and minutes.
 */
export const DateRangeFilter = ({
  value,
  onChange,
  withTime = false,
}: DateRangeFilterProps) => {
  const update = (end: 'from' | 'to', picked: Dayjs | null) => {
    const next = { ...value, [end]: toDate(picked) };
    onChange(next.from || next.to ? next : undefined);
  };

  const picker = (end: 'from' | 'to', placeholder: string) => {
    const common = {
      value: toDayjs(value?.[end]),
      // A picked value commits on accept. A typed value commits when it is complete and valid.
      onAccept: (picked: Dayjs | null) => update(end, picked),
      onChange: (
        picked: Dayjs | null,
        context: { validationError: unknown },
      ) => {
        if (!context.validationError) update(end, picked);
      },
      slotProps: pickerSlotProps(placeholder),
    };
    return withTime ? (
      <DateTimePicker {...common} format="DD/MM/YYYY HH:mm" ampm={false} />
    ) : (
      <DatePicker {...common} format="DD/MM/YYYY" />
    );
  };

  return (
    // marginTop moves the pickers 2px up, to the line of the other column filters, without changing their size.
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginTop: '-2px',
      }}
    >
      {picker('from', 'From')}
      {picker('to', 'To')}
    </div>
  );
};
